import { Divider, Text } from "react-native-paper";
import type { IconType } from "../../../../icon";
import { useAppTheme } from "../../../../useAppTheme";
import { AppIcon } from "../../../AppIcon";
import Box from "../../../Box";
import { MicCheckControls, type MicCheckState } from "./MicCheckControls";
import { PreTestCard } from "./PreTestCard";

export type BrowserCheckContent = {
	title: string;
	label: string;
	browserName: string;
	browserIcon?: IconType;
};

export type MicrophoneCheckContent = {
	title: string;
	intro: string;
	steps: string[];
	successMessage: string;
};

type Props = {
	browserCheck?: BrowserCheckContent;
	microphoneCheck: MicrophoneCheckContent;
	micState: MicCheckState;
	isPlaying?: boolean;
	onRecord?: () => void;
	onStop?: () => void;
	onPlayback?: () => void;
	onRecordAgain?: () => void;
};

const BrowserMicCheck = ({
	browserCheck,
	microphoneCheck,
	micState,
	isPlaying,
	onRecord,
	onStop,
	onPlayback,
	onRecordAgain,
}: Props) => {
	const theme = useAppTheme();
	const outer = theme.roundness * 3;
	const inner = theme.roundness;

	const micCardRadius = browserCheck
		? {
				borderTopLeftRadius: inner,
				borderTopRightRadius: inner,
				borderBottomLeftRadius: outer,
				borderBottomRightRadius: outer,
			}
		: {
				borderTopLeftRadius: outer,
				borderTopRightRadius: outer,
				borderBottomLeftRadius: outer,
				borderBottomRightRadius: outer,
			};

	return (
		<Box gap={4}>
			{browserCheck ? (
				<PreTestCard
					title={browserCheck.title}
					gap={16}
					style={{
						borderTopLeftRadius: outer,
						borderTopRightRadius: outer,
						borderBottomLeftRadius: inner,
						borderBottomRightRadius: inner,
					}}
				>
					<Box
						flexDirection="row"
						alignItems="center"
						justifyContent="space-between"
					>
						<Text variant="bodyLarge">{browserCheck.label}</Text>
						<Box flexDirection="row" alignItems="center" gap={8}>
							<AppIcon
								name={browserCheck.browserIcon ?? "web"}
								size={24}
								color={theme.colors.inverseSurface}
							/>
							<Text
								variant="bodyLarge"
								style={{ color: theme.colors.inverseSurface }}
							>
								{browserCheck.browserName}
							</Text>
						</Box>
					</Box>
				</PreTestCard>
			) : null}

			<PreTestCard title={microphoneCheck.title} gap={16} style={micCardRadius}>
				<Text variant="bodyLarge">{microphoneCheck.intro}</Text>

				<Box gap={8}>
					{microphoneCheck.steps.map((step, index) => (
						<Box
							// biome-ignore lint/suspicious/noArrayIndexKey: static ordered steps
							key={index}
							flexDirection="row"
							gap={8}
							alignItems="flex-start"
						>
							<Text variant="bodyLarge" style={{ minWidth: 18 }}>{`${
								index + 1
							}.`}</Text>
							<Text variant="bodyLarge" style={{ flex: 1 }}>
								{step}
							</Text>
						</Box>
					))}
				</Box>

				<Divider />

				<MicCheckControls
					state={micState}
					successMessage={microphoneCheck.successMessage}
					isPlaying={isPlaying}
					onRecord={onRecord}
					onStop={onStop}
					onPlayback={onPlayback}
					onRecordAgain={onRecordAgain}
				/>
			</PreTestCard>
		</Box>
	);
};

export default BrowserMicCheck;
